import React, { FC, MutableRefObject } from 'react';
import { TextInput, View } from 'react-native';
import ColorWrapper, { WithColor } from '@styles/colors';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { scale } from '@components/frame';
import { isWeb } from '@helpers';
import { TextAnnotation, TextEditingRequest, Viewport } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import type { ImageViewTransform } from '@v2/components/imageHandling/components/DrawingCanvas/helpers/imageViewTransformStore';
import { useManageTextInputOverlay } from './hooks/useManageTextInputOverlay';

interface TextInputOverlayProps extends WithColor {
  editing: TextEditingRequest | null;
  annotation: TextAnnotation | null;
  value: string;
  onChange: (value: string) => void;
  onCommit: () => void;
  onCancel: () => void;
  inputRef: MutableRefObject<HTMLInputElement | null>;
  /** Container rect (getBoundingClientRect) used on web to position the overlay. */
  containerRect: DOMRect | null;
  /** Current SVG CTM used on web to mirror the zoom/pan transform applied to the image. */
  svgCtm: DOMMatrix | null;
  /** Native fallback — the drawing surface viewport + image zoom/pan, used to anchor existing text edits at their saved location. */
  viewport: Viewport;
  viewTransform: ImageViewTransform;
}

const TextInputOverlay: FC<TextInputOverlayProps> = ({
  editing,
  annotation,
  value,
  onChange,
  onCommit,
  onCancel,
  inputRef,
  containerRect,
  svgCtm,
  viewport,
  viewTransform,
}) => {
  const { position, onFocus, onBlur, onKeyPress } = useManageTextInputOverlay({
    editing,
    annotation,
    containerRect,
    svgCtm,
    viewport,
    viewTransform,
    onCommit,
    onCancel,
  });

  if (!editing || !annotation) return null;

  const { left, top, fontSize } = position;
  const inputWidth = Math.max((value.length + 2) * fontSize * 0.6, scale(60));

  return (
    <View
      testID={TEST_IDS.drawingCanvas.textInputOverlay}
      style={{ position: 'absolute', left, top, zIndex: 100, minWidth: scale(60), width: inputWidth }}
    >
      <TextInput
        key={editing.annotationId}
        ref={(el) => {
          // RN Web: TextInput's ref exposes the underlying input via findNodeHandle; the ref itself is enough for focus.
          inputRef.current = el as unknown as HTMLInputElement | null;
        }}
        value={value}
        onChangeText={onChange}
        onSubmitEditing={onCommit}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyPress={onKeyPress as never}
        multiline={false}
        includeFontPadding={false}
        style={{
          fontSize,
          fontWeight: '600',
          color: annotation.style.strokeColor,
          padding: 0,
          minWidth: scale(60),
          width: inputWidth,
          // Web uses a tight line box so the input glyphs align with the SVG render. Native needs
          // headroom for ascenders/descenders or the top of glyphs gets clipped.
          lineHeight: isWeb ? fontSize : fontSize * 1.4,
          height: isWeb ? fontSize : fontSize * 1.4,
          textAlignVertical: isWeb ? 'top' : 'center',
          // Drop the browser focus outline — the existing text styling + dashed selection box already
          // signal that the input is active.
          outlineStyle: 'none',
          borderWidth: 0,
        }}
      />
    </View>
  );
};

export default ColorWrapper(TextInputOverlay);
